import axios from 'axios';
import https from 'https';
import { Bikepark, Snowpark, Pumptrack, Page, Trail, StrapiResponse } from '@/types';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:1337';

// Disable SSL verification for local ddev development (self-signed certs)
const isLocalDev = API_URL.includes('.ddev.site') || API_URL.includes('localhost');
const httpsAgent = new https.Agent({
  rejectUnauthorized: false,
});

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  httpsAgent: isLocalDev ? httpsAgent : undefined,
});

export async function getBikeparks(locale: string = 'pl'): Promise<Bikepark[]> {
  try {
    const url = `${API_URL}/api/bikeparks`;
    // Always use 'pl' locale for Strapi - content is only in Polish
    const params = {
      'pagination[pageSize]': 100,
      'sort': 'name:asc',
      'locale': 'pl',
    };
    console.log('[API] Fetching bikeparks from:', url);
    console.log('[API] With params:', params);
    console.log('[API] Full URL:', url + '?' + new URLSearchParams(params as any).toString());

    const response = await api.get<StrapiResponse<Bikepark>>('/bikeparks', {
      params: params,
    });
    console.log('[API] Bikeparks response:', response.data);
    console.log('[API] Bikeparks count:', response.data.data.length);

    // Try without locale if empty
    if (response.data.data.length === 0) {
      console.log('[API] Empty response, trying without locale...');
      const response2 = await api.get<StrapiResponse<Bikepark>>('/bikeparks', {
        params: {
          'pagination[pageSize]': 100,
          'sort': 'name:asc',
        },
      });
      console.log('[API] Bikeparks without locale:', response2.data);
      return response2.data.data;
    }

    return response.data.data;
  } catch (error) {
    console.error('[API] Error fetching bikeparks:', error);
    return [];
  }
}

export async function getBikeparkBySlug(slug: string, locale: string = 'pl'): Promise<Bikepark | null> {
  try {
    // Use direct slug endpoint instead of filters
    const response = await api.get<{ data: Bikepark }>(`/bikeparks/${slug}`, {
      params: {
        'locale': locale,
      },
    });

    return response.data.data || null;
  } catch (error: any) {
    // 404 is expected for non-existent bikeparks
    if (error.response?.status === 404) {
      return null;
    }
    console.error('Error fetching bikepark:', error);
    return null;
  }
}

export async function getPumptracks(locale: string = 'pl'): Promise<Pumptrack[]> {
  try {
    const url = `${API_URL}/api/pumptracks`;
    const params = {
      'pagination[pageSize]': 500,
      'sort': 'name:asc',
      'locale': 'pl',
    };
    console.log('[API] Fetching pumptracks from:', url);
    console.log('[API] With params:', params);

    const response = await api.get<StrapiResponse<Pumptrack>>('/pumptracks', {
      params: params,
    });
    console.log('[API] Pumptracks response:', response.data);
    console.log('[API] Pumptracks count:', response.data.data.length);

    // Try without locale if empty
    if (response.data.data.length === 0) {
      console.log('[API] Empty response, trying without locale...');
      const response2 = await api.get<StrapiResponse<Pumptrack>>('/pumptracks', {
        params: {
          'pagination[pageSize]': 500,
          'sort': 'name:asc',
        },
      });
      console.log('[API] Pumptracks without locale:', response2.data);
      return response2.data.data;
    }

    return response.data.data;
  } catch (error) {
    console.error('[API] Error fetching pumptracks:', error);
    return [];
  }
}

export async function getPumptrackBySlug(slug: string, locale: string = 'pl'): Promise<Pumptrack | null> {
  try {
    const response = await api.get<{ data: Pumptrack }>(`/pumptracks/${slug}`, {
      params: { 'locale': locale },
    });
    return response.data.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching pumptrack:', error);
    return null;
  }
}

export async function getSnowparks(locale: string = 'pl'): Promise<Snowpark[]> {
  try {
    console.log('[API] Fetching snowparks from:', `${API_URL}/api/snowparks`);

    // Fetch all pages if needed
    let allSnowparks: Snowpark[] = [];
    let page = 1;
    let hasMore = true;
    const pageSize = 100;

    while (hasMore) {
      const response = await api.get<StrapiResponse<Snowpark>>('/snowparks', {
        params: {
          'pagination[page]': page,
          'pagination[pageSize]': pageSize,
          'sort': 'name:asc',
          'locale': 'pl',
        },
      });

      allSnowparks = [...allSnowparks, ...response.data.data];

      const pagination = response.data.meta?.pagination;
      hasMore = pagination ? page < pagination.pageCount : false;
      page++;

      console.log(`[API] Fetched page ${page - 1}, total so far: ${allSnowparks.length}`);

      // Safety limit
      if (page > 10) {
        console.warn('[API] Reached page limit of 10');
        break;
      }
    }

    console.log('[API] Total snowparks fetched:', allSnowparks.length);
    return allSnowparks;
  } catch (error) {
    console.error('[API] Error fetching snowparks:', error);
    return [];
  }
}

export async function getSnowparkBySlug(slug: string, locale: string = 'pl'): Promise<Snowpark | null> {
  try {
    const response = await api.get<{ data: Snowpark }>(`/snowparks/${slug}`, {
      params: { 'locale': locale },
    });
    return response.data.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('Error fetching snowpark:', error);
    return null;
  }
}

export async function getPages(locale: string = 'pl'): Promise<Page[]> {
  try {
    const response = await api.get<StrapiResponse<Page>>('/pages', {
      params: {
        'pagination[pageSize]': 25,
        'sort': 'category:asc',
        'locale': 'pl',
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('Error fetching pages:', error);
    return [];
  }
}

export async function getPageBySlug(slug: string, locale: string = 'pl'): Promise<Page | null> {
  try {
    const response = await api.get<StrapiResponse<Page>>('/pages', {
      params: {
        'filters[slug][$eq]': slug,
        'locale': 'pl',
      },
    });
    return response.data.data[0] || null;
  } catch (error) {
    console.error('Error fetching page:', error);
    return null;
  }
}

export async function getTrails(locale: string = 'pl'): Promise<Trail[]> {
  try {
    const response = await api.get<StrapiResponse<Trail>>('/trails', {
      params: {
        'pagination[pageSize]': 100,
        'sort': 'name:asc',
        'locale': 'pl',
        'populate': 'bikepark',
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('[API] Error fetching trails:', error);
    return [];
  }
}

export async function getTrailBySlug(slug: string, locale: string = 'pl'): Promise<Trail | null> {
  try {
    // Try with requested locale first
    let response = await api.get<StrapiResponse<Trail>>('/trails', {
      params: {
        'filters[slug][$eq]': slug,
        'locale': 'pl',
        'populate': 'bikepark',
      },
    });

    let trails = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
    let trail = trails[0];

    // If not found with pl locale, fallback to en
    if (!trail && locale === 'pl') {
      response = await api.get<StrapiResponse<Trail>>('/trails', {
        params: {
          'filters[slug][$eq]': slug,
          'locale': 'en',
          'populate': 'bikepark',
        },
      });
      trails = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
      trail = trails[0];
    }

    if (!trail) return null;

    return trail;
  } catch (error) {
    console.error('Error fetching trail:', error);
    return null;
  }
}

export async function getTrailsByBikeparkId(bikeparkId: string, locale: string = 'pl', excludeSlug?: string): Promise<Trail[]> {
  try {
    const response = await api.get<StrapiResponse<Trail>>('/trails', {
      params: {
        'filters[bikepark][documentId][$eq]': bikeparkId,
        'locale': 'pl',
        'populate': 'bikepark',
        'sort': 'difficulty:asc,name:asc',
        'pagination[pageSize]': 50,
      },
    });

    let trails = Array.isArray(response.data.data) ? response.data.data : [];

    // Filter out the current trail if excludeSlug is provided
    if (excludeSlug) {
      trails = trails.filter(t => t.slug !== excludeSlug);
    }

    return trails;
  } catch (error) {
    console.error('[API] Error fetching trails by bikepark:', error);
    return [];
  }
}

/**
 * Submit a trip organization request
 */
export interface TripRequestData {
  name: string;
  email: string;
  phone?: string;
  country?: string;
  groupSize: string;
  duration: string;
  dates?: string;
  experience: string;
  services: string[];
  message?: string;
  locale?: string;
}

export async function submitTripRequest(data: TripRequestData): Promise<{ success: boolean; error?: string }> {
  try {
    const response = await api.post('/trip-requests', {
      data: {
        ...data,
        locale: data.locale || 'pl',
      },
    });

    console.log('[API] Trip request submitted:', response.data);
    return { success: true };
  } catch (error: any) {
    console.error('[API] Error submitting trip request:', error);
    return {
      success: false,
      error: error.response?.data?.error?.message || error.message || 'Unknown error'
    };
  }
}

/**
 * Blog posts
 */
export interface BlogPost {
  id: number;
  documentId: string;
  title: string;
  slug: string;
  excerpt: string;
  content?: string;
  author: string;
  publishedAt: string;
  readingTime: number;
  featured: boolean;
  difficulty?: string;
  season?: string;
  cover?: string;
  featuredImage?: { url: string; alternativeText?: string };
  createdAt: string;
  updatedAt: string;
}

export async function getBlogPosts(locale: string = 'pl'): Promise<BlogPost[]> {
  try {
    const response = await api.get<StrapiResponse<BlogPost>>('/blog', {
      params: {
        'sort': 'publishedAt:desc',
        'locale': locale,
      },
    });
    return response.data.data;
  } catch (error) {
    console.error('[API] Error fetching blog posts:', error);
    return [];
  }
}

export async function getBlogPostBySlug(slug: string, locale: string = 'pl'): Promise<BlogPost | null> {
  try {
    const response = await api.get<{ data: BlogPost }>(`/blog/${slug}`, {
      params: { 'locale': locale },
    });
    return response.data.data || null;
  } catch (error: any) {
    if (error.response?.status === 404) return null;
    console.error('[API] Error fetching blog post:', error);
    return null;
  }
}

export default api;
